<template>
  <div class="container">
    <!-- 页面标题 -->
    <header class="header-section">
      <h1 class="page-title">学校分数区间查询</h1>
      <p class="page-description">
        输入您的分数，查看在各学校的分数区间中的位置，助您更好地规划升学选择
      </p>
    </header>

    <!-- 选择区域 -->
    <el-card class="query-card">
      <template #header>
        <div class="card-header">
          <i class="fa fa-filter icon-primary"></i>
          <span>查询条件</span>
        </div>
      </template>

      <!-- 查询表单 -->
      <el-form :model="queryForm" label-width="80px" class="query-form">
        <!-- 省份选择 -->
        <div class="form-group">
          <label class="form-label">省份</label>
          <el-select v-model="queryForm.province" placeholder="请选择省份">
            <el-option
              v-for="item in provinces"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>

        <!-- 年份选择 -->
        <div class="form-group">
          <label class="form-label">年份</label>
          <el-select v-model="queryForm.year" placeholder="请选择年份">
            <el-option
              v-for="item in years"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>

        <!-- 学科类别选择 -->
        <div class="form-group">
          <label class="form-label">学科类别</label>
          <el-select v-model="queryForm.subject" placeholder="请选择学科类别">
            <el-option
              v-for="item in subjects"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>

        <!-- 性别选择 -->
        <div class="form-group">
          <label class="form-label">性别</label>
          <el-select v-model="queryForm.gender" placeholder="请选择性别">
            <el-option
              v-for="item in genders"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>
      </el-form>

      <!-- 分数输入 -->
      <div class="score-input-container">
        <el-form :model="queryForm" label-width="80px" class="score-form">
          <div class="form-group">
            <label class="form-label">输入分数</label>
            <el-input
              v-model.number="queryForm.score"
              placeholder="请输入您的分数"
              prefix-icon="el-icon-graduation-cap"
            >
              <template #append>分</template>
            </el-input>
          </div>
        </el-form>
        <el-button type="primary" @click="queryScore" class="query-btn">
          <i class="fa fa-search mr-2"></i>查询
        </el-button>
      </div>
    </el-card>
    <!-- 结果区域 -->
    <el-card class="result-card">
      <template #header>
        <div class="card-header">
          <i class="fa fa-bar-chart icon-primary"></i>
          <span>学校分数区间分布</span>
        </div>
      </template>

      <!-- 虚线说明 -->
      <p class="dashed-line-explanation">
        <strong>------:</strong>表示您的分数:
        <span class="score-value">{{ queryForm.score }}</span
        >分
      </p>
      <!-- 图表容器 -->
      <div class="chart-container" v-loading="loading">
        <canvas ref="chartRef"></canvas>
      </div>
      <!-- 注的内容 -->
      <p class="note-text">注：录取分数已公开数据，不区分性别和学科类别</p>
      <!-- 分数说明 -->
      <!-- <el-alert
        v-show="showResult"
        title="分数分析"
        type="info"
        :content="resultText"
        :closable="false"
        class="analysis-result"
      /> -->
    </el-card>

    <!-- 加载状态 -->
    <el-loading v-model="loading" size="large" class="loading-mask" />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, onUnmounted } from "vue";
import { baseURL } from "@/api/axiosConfig";
import {
  ElCard,
  ElForm,
  ElFormItem,
  ElSelect,
  ElOption,
  ElInput,
  ElButton,
  ElAlert,
  ElMessage,
  ElLoading,
} from "element-plus";
import "element-plus/dist/index.css";
import Chart from "chart.js/auto";
import axios from "axios"; // 引入axios用于HTTP请求

// 表单数据
const queryForm = reactive({
  province: "河北省",
  year: "2024",
  subject: "物理",
  gender: "男生",
  score: null,
});

// 加载状态
const loading = ref(false);

// 选项数据
const provinces = [
  { value: "hebei", label: "河北" },
  // { value: "beijing", label: "北京" },
  // { value: "shanghai", label: "上海" },
  // { value: "guangdong", label: "广东" },
  // { value: "shandong", label: "山东" },
];
//年份
const years = [
  { value: "2024", label: "2024年" },
  { value: "2023", label: "2023年" },
  { value: "2022", label: "2022年" },
];
//学科类别 (type)
const subjects = [
  { value: "物理", label: "物理类" },
  { value: "历史", label: "历史类" },
];
//性别 (sex)
const genders = [
  { value: "男生", label: "男生" },
  { value: "女生", label: "女生" },
];

// 图表引用和实例
const chartRef = ref(null);
let scoreChart = null;

// 结果显示
const showResult = ref(false);
// const resultText = ref("");

// 后端接口URL
const API_URL = baseURL + "/interface/searchscore/getSearchscore";

// 初始化图表
const initChart = (data = [], userScore = null) => {
  if (!chartRef.value) return;

  if (scoreChart) scoreChart.destroy();

  if (data.length === 0) {
    ElMessage.warning("未获取到数据，请检查查询条件");
    return;
  }

  // 过滤有效数据
  const validData = data.filter((school) => {
    return (
      school.score !== undefined &&
      school.minscore !== undefined &&
      school.maxscore !== undefined
    );
  });

  if (validData.length === 0) {
    ElMessage.warning("未获取到有效分数数据，请检查数据源");
    return;
  }

  const schools = validData.map((school) => school.schoolname);
  const scoresMin = validData.map((school) => Number(school.score));
  const scoresMinRange = validData.map((school) => Number(school.minscore));
  const scoresMaxRange = validData.map((school) => Number(school.maxscore));
  const avgScores = validData.map((school) => Number(school.avgscore)); // 新增平均分
  //排名区间
  const maxRankings = validData.map((school) => school.maxranking || "N/A");
  const minRankings = validData.map((school) => school.minranking || "N/A");
  //最高分排名
  //最低分排名
  //最后和x轴数据拼起来

  // 计算Y轴最小值
  const minScore = Math.min(
    ...scoresMin,
    ...scoresMinRange,
    ...avgScores.filter((score) => score !== null)
  );
  const yMin = Math.max(450, Math.floor(minScore / 50) * 50);

  // 构造原始柱状图数据 [score, 750]
  const intervalData1 = schools.map((school, i) => ({
    x: school,
    y: [scoresMin[i], 750],
  }));

  // 构造新柱状图数据 [minscore, maxscore]
  const intervalData2 = schools.map((school, i) => ({
    x: school,
    y: [scoresMinRange[i], scoresMaxRange[i]],
  }));

  // 根据用户分数判断颜色（原柱子）
  const backgroundColors1 = scoresMin.map((min) => {
    if (userScore !== null && userScore < min) {
      return "rgba(255, 0, 0, 0.6)";
    }
    return "rgba(22, 93, 255, 0.6)";
  });

  // 新柱子颜色统一设置为绿色系
  const backgroundColors2 = Array(scoresMinRange.length).fill(
    "rgba(46, 204, 113, 0.6)"
  );

  const ctx = chartRef.value.getContext("2d");

  scoreChart = new Chart(ctx, {
    type: "bar",
    data: {
      labels: schools,
      datasets: [
        {
          label: "与该区域交叉，则达到参加军检考生最低分数线",
          data: intervalData1,
          parsing: { xAxisKey: "x", yAxisKey: "y" },
          backgroundColor: backgroundColors1,
          borderColor: "rgba(22, 93, 255, 1)",
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.6,
          categoryPercentage: 0.8,
        },
        {
          label: "与该区域交叉，则达到实际录取分数区间",
          data: intervalData2,
          parsing: { xAxisKey: "x", yAxisKey: "y" },
          backgroundColor: backgroundColors2,
          borderColor: "rgba(46, 204, 113, 1)",
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.6,
          categoryPercentage: 0.8,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top" },
        tooltip: {
          callbacks: {
            label: function (context) {
              const datasetIndex = context.datasetIndex;
              const schoolIndex = context.dataIndex;
              const school = validData[schoolIndex];

              if (datasetIndex === 0) {
                return `军检最低分: ${school.score}`;
              } else if (datasetIndex === 1) {
                let result = `实际区间: ${school.minscore} ~ ${school.maxscore}`;
                if (school.avgscore !== null && school.avgscore !== undefined) {
                  result += `\n平均分: ${school.avgscore}`;
                }
                // 添加排名区间
                result += `\n排名位次: ${minRankings[schoolIndex]} ~ ${maxRankings[schoolIndex]}`;
                return result;
              }
              return "";
            },
          },
        },
        annotation: {
          annotations: validData.reduce((acc, school, index) => {
            if (school.avgscore !== null && school.avgscore !== undefined) {
              acc[`avgLine${index}`] = {
                type: "line",
                xMin: index + 0.1, // 控制线的左边界，限制在线条所在柱子的宽度内
                xMax: index + 0.3, // 控制线的右边界
                yMin: school.avgscore,
                yMax: school.avgscore,
                borderColor: "rgb(255, 165, 0)", // 黄色虚线
                borderWidth: 3,
                borderDash: [], // 实线
                label: {
                  display: false,
                  content: `平均分: ${school.avgscore}`,
                  position: "right",
                },
              };
            }
            return acc;
          }, {}),
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            font: { style: "italic", size: 12 },
            autoSkip: false,
            maxRotation: 45,
            minRotation: 30,
          },
        },
        y: {
          beginAtZero: false,
          min: yMin,
          max: 750,
          ticks: { stepSize: 50 },
        },
      },
      layout: {
        padding: { left: 20, right: 20, top: 10, bottom: 80 },
      },
      animation: { duration: 1000, easing: "easeOutQuart" },
    },
  });
};
// 添加分数虚线
const addScoreLine = (score) => {
  if (!scoreChart) return;

  // 获取 annotation 插件配置对象
  const annotations = scoreChart.options.plugins.annotation.annotations;
  // 设置水平参考线
  annotations.horizontalLine = {
    type: "line",
    yMin: score,
    yMax: score,
    borderColor: "rgb(250,0,0)", // 红色虚线
    borderWidth: 3,
    borderDash: [5, 5], // 虚线样式
    label: {
      display: false,
      content: `您的分数: ${score}`,
      position: "right",
      color: "#000",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      font: {
        weight: "bold",
        size: 16,
      },
    },
  };

  scoreChart.update(); // 更新图表
};

// 分析分数并显示结果
// const analyzeScore = (score, schoolData) => {
//   console.log("分析分数:", score, "学校数据长度:", schoolData.length);

//   if (schoolData.length === 0) {
//     resultText.value = "未获取到学校数据，请检查查询条件或联系管理员";
//     showResult.value = true;
//     return;
//   }

//   let aboveSchools = 0;
//   let withinSchools = 0;
//   let belowSchools = 0;
//   const aboveSchoolList = [];
//   const withinSchoolList = [];

//   // 假设后端返回的score是最低分，这里需要根据实际情况调整
//   // 假设每个学校的分数区间是 [score, score+30]，实际应根据后端数据结构调整
//   schoolData.forEach((school) => {
//     const schoolMin = school.score;
//     const schoolMax = school.score + 30; // 假设分数区间为30分

//     if (score > schoolMax) {
//       aboveSchools++;
//       aboveSchoolList.push(school.schoolname);
//     } else if (score >= schoolMin && score <= schoolMax) {
//       withinSchools++;
//       withinSchoolList.push(school.schoolname);
//     } else {
//       belowSchools++;
//     }
//   });

//   // let result = `您的分数 ${score} 分，在${queryForm.year}年${
//   //   queryForm.subject === "physics" ? "物理类" : "历史类"
//   // }中：<br>`;

//   if (aboveSchools > 0)
//     result += `<span class="green-text">高于</span> ${aboveSchools} 所学校的录取区间，包括：${aboveSchoolList
//       .slice(0, 5) // 限制显示数量
//       .join("、")}${aboveSchoolList.length > 5 ? "等" : ""}；<br>`;
//   if (withinSchools > 0)
//     result += `<span class="blue-text">在</span> ${withinSchools} 所学校的录取区间内，包括：${withinSchoolList
//       .slice(0, 5) // 限制显示数量
//       .join("、")}${withinSchoolList.length > 5 ? "等" : ""}；<br>`;
//   if (belowSchools > 0)
//     result += `<span class="red-text">低于</span> ${belowSchools} 所学校的最低录取分。`;

//   resultText.value = result;
//   showResult.value = true;

//   setTimeout(() => {
//     const resultElement = document.querySelector(".analysis-result");
//     if (resultElement) resultElement.scrollIntoView({ behavior: "smooth" });
//   }, 100);
// };

// 验证分数输入
const validateScore = () => {
  const { score } = queryForm;
  if (isNaN(score) || score < 0 || score > 750) {
    ElMessage.error("请输入有效的分数(0-750)");
    return false;
  }
  return true;
};

// 从后端获取数据
const fetchSchoolData = async () => {
  try {
    loading.value = true;
    const response = await axios.get(
      API_URL +
        "?year=" +
        queryForm.year +
        "&&sex=" +
        queryForm.gender +
        "&&type=" +
        queryForm.subject
    );

    if (response.data && response.data.code === 200) {
      const schoolData = response.data.data || [];
      if (schoolData.some((school) => school.score === undefined)) {
        ElMessage.warning("部分数据缺少最低分字段，请检查数据源");
      }
      initChart(schoolData);
      return schoolData;
    } else {
      ElMessage.error(response.data?.message || "获取数据失败，请重试");
      return [];
    }
  } catch (error) {
    console.error("获取数据失败:", error);
    ElMessage.error("网络错误，请检查网络连接");
    return [];
  } finally {
    loading.value = false;
  }
};
// 查询分数
const queryScore = async () => {
  console.log("查询分数:", queryForm.score);
  if (!validateScore()) return;

  // 先获取学校数据
  const schoolData = await fetchSchoolData();

  if (schoolData.length > 0) {
    initChart(schoolData, queryForm.score); // 传递用户分数
    addScoreLine(queryForm.score);
    //analyzeScore(queryForm.score, schoolData);
  }
};

onMounted(() => {
  // 页面加载时先获取数据
  fetchSchoolData();
});

watch(
  [
    () => queryForm.province,
    () => queryForm.year,
    () => queryForm.subject,
    () => queryForm.gender,
  ],
  () => {
    // 当查询条件变化时，重新获取数据
    fetchSchoolData();
    showResult.value = false;
  }
);

onUnmounted(() => {
  // 组件卸载时销毁图表，避免内存泄漏
  if (scoreChart) {
    scoreChart.destroy();
    scoreChart = null;
  }
});
</script>

<style scoped>
.container {
  width: 1200px;
  margin: auto;
  margin-top: 50px;
  position: relative;
}

.header-section {
  text-align: center;
  margin-bottom: 2rem;
  margin-top: 2rem; /* 新增：确保与顶部导航栏有间距 */
}

.page-title {
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  font-weight: bold;
  color: #165dff;
  margin-bottom: 0.5rem;
}

.page-description {
  color: #6b7280;
  max-width: 600px;
  margin: 0 auto 2rem;
}

.query-card {
  margin-bottom: 2rem;
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.icon-primary {
  color: #165dff;
  margin-right: 0.5rem;
}

.query-form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 14px;
  margin-bottom: 0.5em;
}

.score-input-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1.5rem;
}

.score-form {
  flex: 1;
  min-width: 250px;
}

.query-btn {
  align-self: flex-end;
}

.chart-container {
  height: 400px;
  position: relative;
}
@media (min-width: 768px) {
  .chart-container {
    height: 500px;
  }
}

.analysis-result {
  margin-top: 1.5rem;
}

.green-text {
  color: #10b981;
  font-weight: 500;
}

.blue-text {
  color: #2563eb;
  font-weight: 500;
}

.red-text {
  color: #ef4444;
  font-weight: 500;
}

.page-footer {
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
  margin-top: 3rem;
  padding-bottom: 1rem;
}

.loading-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dashed-line-explanation {
  margin: 1rem 0;
  font-size: 0.9rem;
  color: #6b7280;
  text-align: center;
}

.dashed-line-explanation strong {
  color: red;
}

.dashed-line-explanation span.score-value {
  color: #ef4444; /* 红色 */
  font-weight: bold;
}
/* 注的样式 */
.note-text {
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #6b7280;
  text-align: right; /* 右对齐 */
}
</style>
